import type { MixedLineBarChartProps } from '@cloudscape-design/components/mixed-line-bar-chart';
import type { DerivedMetricPoint } from '../api/contracts';

type LineSegment = MixedLineBarChartProps.LineDataSeries<Date>;

/**
 * Cloudscape's LineChart cannot draw a `type: 'line'` segment that has
 * exactly one point -- its scale computation for that segment comes back
 * NaN, which React then renders as an invalid empty-string x1/x2 SVG
 * attribute (visible in the browser console as "Invalid value for <line>
 * attribute x1=''"). `toGappedLineSegments`/`toResampledLineSegments`
 * legitimately produce single-point segments (an isolated reading between
 * two gaps), so rather than dropping that real reading, nudge it into a
 * two-point segment with a 1ms-later duplicate at the same y -- Cloudscape
 * then draws a segment so short it reads as a single dot, which is exactly
 * what a lone reading should look like.
 */
export function makeSegmentRenderable<T extends { x: Date; y: number }>(data: readonly T[]): T[] {
  if (data.length !== 1) return [...data];
  const only = data[0]!;
  return [only, { ...only, x: new Date(only.x.getTime() + 1) }];
}

/**
 * Converts one metric of a today-summary `series` into one or more
 * Cloudscape LineChart segments.
 *
 * Why segments, not a single series with holes: Cloudscape's chart data
 * point type (`MixedLineBarChartProps.Datum`) requires a numeric `y` — there
 * is no `null`/`undefined` sentinel the chart understands as "no data here".
 * Passing 0 (or interpolating between neighbors) would misrepresent a
 * missing reading as a real dip to zero, which is exactly what the energy
 * model's per-field absence (a key genuinely missing from a
 * `DerivedMetricPoint`) exists to prevent (see docs/PLAN.md's energy model
 * section and DerivedMetricPoint's field doc comments).
 *
 * The fix used here: split the run of points into contiguous chunks,
 * breaking the chunk whenever the accessor comes back `undefined` for a
 * point. Each chunk becomes its own line-chart series sharing the same
 * title/color, so the rendered line visibly stops and restarts at the gap
 * instead of being smoothed over. Tradeoff: if a day has several gaps,
 * Cloudscape's legend may list the series title more than once (one entry
 * per chunk) since the chart has no concept of "same series, multiple
 * runs" — accepted here since a duplicated legend label is far less
 * misleading than a fabricated data point.
 *
 * Deliberately does NOT also break on `point.isPartial` (removed
 * 2026-07-28 -- see git history for the incident this caused): `isPartial`
 * is a point-LEVEL flag that can be true for a reason entirely unrelated to
 * the specific field being plotted here -- e.g. compute.ts sets it whenever
 * SHP2's grid/load pair is incomplete for that minute, which happens on
 * roughly half of all minutes given SHP2's ~2-minute poll cadence, even
 * though `gridImportW` (mains) is independently anchored and correct for
 * EVERY point that gets emitted at all (see compute.ts's own
 * `ComputedDerivedPoint.gridImportW` doc comment). Gating every field on
 * the blanket flag made Grid Import (and Total consumption) falsely gap
 * out for hours at a time on live data, even though those two fields' own
 * values were present and correct throughout. Each field's own presence is
 * already exactly tracked by whether the accessor returns a value at all
 * (compute.ts never writes a field to the point unless it's genuinely
 * present) -- trusting that per-field signal, rather than the point-level
 * flag, is both simpler and correct.
 */
export function toGappedLineSegments(
  points: readonly DerivedMetricPoint[],
  accessor: (point: DerivedMetricPoint) => number | undefined,
  title: string,
  color?: string,
): LineSegment[] {
  const segments: LineSegment[] = [];
  let current: { x: Date; y: number }[] = [];

  // Cloudscape's legend renders one row per series in the `series` array,
  // unconditionally, with no dedup-by-title and no prop to suppress a
  // specific series from it (checked: neither LineChartProps nor its
  // CommonMixedChartProps/CartesianChartProps ancestors expose a
  // hide-from-legend flag). A sparse metric can produce many same-titled
  // segments, so give the real title to only the FIRST segment; every later
  // segment of the SAME metric gets an empty title, which the legend
  // effectively skips (it still renders the swatch, but with the metric
  // name shown only once). The alternative -- feeding NaN as a `y` value to
  // fake a single continuous series -- was considered and rejected: the
  // type is `y: number`, not `number | null`, and Cloudscape documents no
  // gap-break behavior for it, so relying on however its internal d3 path
  // generator happens to handle NaN today would be relying on unspecified
  // behavior for the exact correctness property (never visually bridge a
  // real gap) this code exists to guarantee.
  let titledOnce = false;

  const flush = () => {
    if (current.length > 0) {
      const segmentTitle = titledOnce ? '' : title;
      titledOnce = true;
      segments.push({ type: 'line', title: segmentTitle, data: current, ...(color ? { color } : {}) });
    }
    current = [];
  };

  for (const point of points) {
    const value = accessor(point);
    if (value === undefined || Number.isNaN(value)) {
      flush();
      continue;
    }
    current.push({ x: new Date(point.ts), y: value });
  }
  flush();

  // Preserve the series/legend entry even on a day with zero usable points.
  return segments.length > 0 ? segments : [{ type: 'line', title, data: [], ...(color ? { color } : {}) }];
}

/**
 * Averages arbitrary timestamped points into fixed-width time buckets.
 *
 * Root cause of the "scattered dots, not a line" chart-readability issue
 * (see docs/PLAN.md): Emporia's mains channel has a genuinely lower
 * per-minute hit rate than its own branch channels (confirmed via a
 * temporary read-only diagnostic, ~38% vs ~77% over a 180-minute sample),
 * so any chart plotting raw per-minute points is capped at mains's own
 * density. Bucketing into coarser buckets and averaging whatever points
 * landed in each one smooths over that inherent per-minute sparsity without
 * touching the underlying 1-minute data -- this is a display-only
 * transform, never applied to what's stored or returned by the API.
 *
 * A bucket with zero usable points is simply absent from the result (never
 * synthesized as 0 or interpolated) -- callers that need "no data here" to
 * render as a visible gap should treat a skipped bucket index as a break,
 * exactly as toGappedLineSegments does for individual missing points.
 */
export function resampleToBuckets<T>(
  points: readonly T[],
  tsOf: (point: T) => string,
  valueOf: (point: T) => number | undefined,
  bucketMs: number,
  /** Optional: when it returns true for ANY point contributing to a bucket, that bucket's result carries `filled: true`. Used to mark buckets that are partly or wholly carried-forward EcoFlow readings (compute.ts's forward-fill) rather than same-minute measurements. */
  filledOf?: (point: T) => boolean,
): { ts: string; value: number; filled: boolean }[] {
  const buckets = new Map<number, { sum: number; count: number; filled: boolean }>();

  for (const point of points) {
    const value = valueOf(point);
    if (value === undefined || Number.isNaN(value)) continue;
    const bucketStart = Math.floor(new Date(tsOf(point)).getTime() / bucketMs) * bucketMs;
    const filled = filledOf?.(point) ?? false;
    const entry = buckets.get(bucketStart);
    if (entry) {
      entry.sum += value;
      entry.count += 1;
      if (filled) entry.filled = true;
    } else {
      buckets.set(bucketStart, { sum: value, count: 1, filled });
    }
  }

  return [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([bucketStart, { sum, count, filled }]) => ({ ts: new Date(bucketStart).toISOString(), value: sum / count, filled }));
}

/** Bucket widths this app resamples to, from finest to coarsest. */
const BUCKET_OPTIONS_MS = [
  60_000, // 1 min
  5 * 60_000,
  15 * 60_000,
  30 * 60_000,
  60 * 60_000,
  3 * 60 * 60_000,
  6 * 60 * 60_000,
  24 * 60 * 60_000,
];

/**
 * Picks the finest bucket width from `BUCKET_OPTIONS_MS` that keeps a chart
 * spanning `spanMs` under roughly `targetBuckets` plotted points -- enough
 * resolution to still look like a trend, coarse enough to smooth over
 * mains's per-minute gaps. Falls back to the coarsest option for very wide
 * ranges (e.g. a 7-day Circuits window).
 */
export function pickBucketMs(spanMs: number, targetBuckets = 96): number {
  const raw = spanMs / targetBuckets;
  return BUCKET_OPTIONS_MS.find((ms) => ms >= raw) ?? BUCKET_OPTIONS_MS[BUCKET_OPTIONS_MS.length - 1]!;
}

/**
 * Lightens a `#rrggbb` color toward white (blend factor 0-1, 0 = unchanged,
 * 1 = white) so a carried-forward/interpolated chart segment reads as the
 * same series at a glance but visibly less certain than a same-minute
 * reading -- Cloudscape's `LineDataSeries` has no dash-style/line-style
 * prop (checked its .d.ts), so color is the only per-segment styling knob
 * this chart library actually exposes.
 */
function muteColor(hex: string, blend = 0.55): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const [r, g, b] = [m[1]!, m[2]!, m[3]!].map((h) => parseInt(h, 16));
  const mix = (c: number) => Math.round(c + (255 - c) * blend);
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(mix(r!))}${toHex(mix(g!))}${toHex(mix(b!))}`;
}

/**
 * Same contract as `toGappedLineSegments` (never bridges a real gap, only
 * the first segment per metric is titled so the legend doesn't repeat, and
 * -- see that function's doc comment -- gates only on the accessor's own
 * per-field presence, never on the point-level `isPartial` flag), but
 * resamples to `bucketMs` buckets first via `resampleToBuckets`. A missing
 * bucket (no points in that minute range had a real value for this
 * specific field) breaks the line.
 *
 * When `filledAccessor` is given, a bucket flips between "real" and
 * "carried-forward" (compute.ts's forward-fill) also breaks the segment,
 * and carried-forward segments render in a lightened tint of `color` via
 * `muteColor` -- see that function's doc comment for why color, not a dash
 * style, is used.
 */
export function toResampledLineSegments(
  points: readonly DerivedMetricPoint[],
  accessor: (point: DerivedMetricPoint) => number | undefined,
  title: string,
  bucketMs: number,
  color?: string,
  filledAccessor?: (point: DerivedMetricPoint) => boolean,
): LineSegment[] {
  const resampled = resampleToBuckets(points, (p) => p.ts, accessor, bucketMs, filledAccessor);

  const segments: LineSegment[] = [];
  let current: { x: Date; y: number }[] = [];
  let currentFilled = false;
  let prevBucketStart: number | null = null;
  let titledOnce = false;
  let lastPoint: { x: Date; y: number } | null = null;

  const flush = () => {
    if (current.length > 0) {
      const segmentTitle = titledOnce ? '' : title;
      titledOnce = true;
      const segmentColor = color ? (currentFilled ? muteColor(color) : color) : undefined;
      segments.push({ type: 'line', title: segmentTitle, data: current, ...(segmentColor ? { color: segmentColor } : {}) });
    }
    current = [];
  };

  for (const { ts, value, filled } of resampled) {
    const bucketStart = new Date(ts).getTime();
    const isTimeGap = prevBucketStart !== null && bucketStart - prevBucketStart > bucketMs;
    const isFilledChange = prevBucketStart !== null && filled !== currentFilled;
    if (isTimeGap || isFilledChange) {
      flush();
      // A filled<->real transition (not a real time gap) should read as one
      // continuous line that changes color, not a visual break -- carry the
      // prior segment's last point over as this segment's first point.
      if (isFilledChange && !isTimeGap && lastPoint) current.push(lastPoint);
    }
    const point = { x: new Date(ts), y: value };
    current.push(point);
    lastPoint = point;
    currentFilled = filled;
    prevBucketStart = bucketStart;
  }
  flush();

  return segments.length > 0 ? segments : [{ type: 'line', title, data: [], ...(color ? { color } : {}) }];
}
